import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Badge,
  Typography,
  Descriptions,
  Row,
  Col,
  Tabs,
  Tag,
  Progress,
} from 'antd';
import {
  EditOutlined,
  ReloadOutlined,
  EyeOutlined,
  KeyOutlined,
  SaveOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import './index.less';
import saveAs from 'file-saver';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

// ─── 78 张卡牌的默认世界地图坐标（等经纬投影百分比，0-100）───
/**
 * 数据库真实 ID → [map_x, map_y]（等经纬投影百分比，0-100）
 * 坐标从 uma.csv 地点数据推算：x=(经度+180)/360×100，y=(90-纬度)/180×100
 */
const DEFAULT_POSITIONS: Record<number, [number, number]> = {
  150: [48.8, 18.2],  // 尼斯湖水怪  苏格兰尼斯湖        57.3°N  4.4°W
  151: [16.1, 25.0],  // 大脚怪      美国西北部           45°N  122°W
  152: [73.9, 34.4],  // 雪人        尼泊尔喜马拉雅       28°N   86°E
  153: [27.5, 28.9],  // 天蛾人      美国西弗吉尼亚       38°N   81°W
  154: [44.4, 19.4],  // 克拉肯      北大西洋             55°N   20°W
  155: [31.7, 40.0],  // 卓柏卡布拉  波多黎各             18°N   66°W
  156: [29.4, 27.8],  // 泽西恶魔    美国新泽西           40°N   74°W
  157: [19.4, 26.1],  // 雷鸟        美国落基山           43°N  110°W
  158: [50.9, 25.2],  // 格沃丹之兽  法国格沃丹          44.7°N   3.2°E
  159: [16.7, 29.6],  // 弗雷斯诺夜行者 美国加州         36.7°N 119.8°W
  160: [30.2, 26.6],  // 多佛恶魔    美国马萨诸塞        42.2°N  71.3°W
  161: [78.9, 25.6],  // 蒙古死亡蠕虫 蒙古戈壁           44°N  104°E
  162: [16.8, 22.3],  // 奥哥波哥    加拿大奥卡纳根湖    49.8°N 119.5°W
  163: [29.6, 25.3],  // 查普湖怪    美加查普湖          44.5°N  73.3°W
  164: [90.3, 68.9],  // 邦尼普      澳大利亚东南部       34°S  145°E
  165: [28.7, 28.3],  // 山羊人      美国马里兰           39°N   76.8°W
  166: [27.2, 35.0],  // 臭鼬猿      美国佛罗里达         27°N   82°W
  167: [55.6, 50.6],  // 莫克勒姆贝姆贝 刚果盆地          1°S   22°E
  168: [50.0, 88.9],  // 宁恩        南极海域             70°S    0°E
  169: [52.8, 24.4],  // 塔佐蠕虫    阿尔卑斯山           46°N   10°E
  170: [87.7, 30.3],  // 椎子蛇      日本                35.4°N 136.9°E
  171: [46.0, 13.9],  // 拉加弗洛特巨蛇 冰岛拉加弗洛特湖  65°N   14.5°W
  172: [15.7, 23.1],  // 卡德波洛龙  加拿大不列颠哥伦比亚 48.5°N 123.5°W
  173: [76.4, 34.4],  // 布鲁巨兽    印度阿鲁纳恰尔邦     28°N   95°E
  174: [26.6, 28.2],  // 洛夫兰蛙人  美国俄亥俄          39.3°N  84.3°W
  175: [33.3, 51.7],  // 马平瓜里    巴西亚马逊           3°S   60°W
  176: [78.3, 51.1],  // 奥让潘德克  印尼苏门答腊         2°S  102°E
  177: [48.7, 21.9],  // 博德明野兽  英国康沃尔          50.5°N   4.7°W
  178: [24.2, 29.7],  // 奥扎克嚎叫兽 美国奥扎克山       36.5°N  93°W
  179: [25.4, 26.3],  // 布雷路野兽  美国威斯康星        42.7°N  88.7°W
  180: [27.6, 28.5],  // 平顶怪      美国西弗吉尼亚      38.7°N  80.7°W
  181: [28.5, 28.1],  // 德威约      美国马里兰          39.5°N  77.5°W
  182: [90.3, 52.8],  // 罗本翼龙    巴布亚新几内亚       5°S  145°E
  183: [80.6, 53.9],  // 阿胡        印尼爪哇岛           7°S  110°E
  184: [56.9, 55.6],  // 空噶玛托    赞比亚/刚果         10°S   25°E
  185: [54.7, 65.8],  // 格鲁特斯朗  南非里希特斯费尔德  28.5°S  17°E
  186: [55.6, 48.9],  // 吉巴福菲    刚果雨林             2°N   20°E
  187: [34.7, 52.8],  // 明霍卡      巴西亚马逊盆地       5°S   55°W
  224: [28.6, 27.2],  // 雅各布斯兽  美国宾夕法尼亚       41°N   77°W
  189: [50.0, 30.0],  // 影子人      全球多地（中心占位）
  190: [26.4, 25.0],  // 汽笛人      北美森林             45°N   85°W
  191: [29.8, 26.8],  // 格洛瓦克斯  美国康涅狄格        41.7°N  72.6°W
  192: [25.7, 29.5],  // 霍普金斯维尔哥布林 美国肯塔基   36.9°N  87.5°W
  193: [61.0, 53.3],  // 波波巴瓦    坦桑尼亚桑给巴尔     6°S   39.4°E
  194: [16.1, 24.3],  // 蝙蝠大脚怪  美国华盛顿州        46.2°N 122.2°W
  195: [49.0, 21.6],  // 埃克斯穆尔野兽 英国埃克斯穆尔   51.1°N   3.6°W
  196: [55.0, 49.4],  // 埃梅拉恩托卡 刚果河流域          1°N   18°E
  197: [15.7, 23.1],  // 西提斯湖怪  加拿大西提斯湖      48.4°N 123.5°W
  198: [49.2, 21.6],  // 嚎叫灵火    英国萨默塞特        51.1°N   2.8°W
  199: [48.6, 22.1],  // 鸮人        英国康沃尔郡        50.3°N   5°W
  221: [18.1, 27.8],  // 空天汽笛    北美悬崖             40°N  115°W
  222: [38.9, 27.8],  // 深海汽笛    北大西洋             40°N   40°W
  223: [6.8,  39.2],  // 火山汽笛    夏威夷岛            19.5°N 155.5°W
  200: [50.0, 21.4],  // 弹簧腿杰克  英国伦敦            51.5°N   0.1°W
  201: [49.7, 21.1],  // 狼人        英国                 52°N    1°W
  213: [22.9, 39.7],  // 特瓦坎狼人  墨西哥特瓦坎        18.5°N  97.4°W
  215: [48.9, 18.6],  // 苏格兰马形水鬼 苏格兰           56.5°N   4°W
  228: [24.7, 33.1],  // 沼泽怪物    美国路易斯安那      30.5°N  91°W
  211: [26.7, 23.9],  // 温迪戈      北美大湖区           47°N   84°W
  206: [26.2, 28.8],  // 教皇利克怪兽 美国肯塔基         38.2°N  85.6°W
  216: [6.7,  38.9],  // 夏威夷魔犬  美国夏威夷          19.5°N 155.5°W
  217: [80.8, 32.3],  // 神农架野人  湖北神农架          31.8°N 110.7°E
  225: [25.3, 27.2],  // 大怪鸟      美国伊利诺伊         41°N   89°W
  226: [72.8, 30.0],  // 昆仑山螳螂人 中国昆仑山          36°N   82°E
  207: [26.4, 25.8],  // 密歇根狗人  美国密歇根          43.5°N  85°W
  202: [86.3, 31.7],  // 河童        日本九州             33°N  130.5°E
  204: [27.7, 31.0],  // 蜥蜴人      美国南卡罗来纳      34.2°N  80.2°W
  212: [56.9, 66.1],  // 南非飞螳人  南非                 29°S   25°E
  218: [74.2, 22.9],  // 喀纳斯湖水怪 新疆喀纳斯湖       48.7°N  87.1°E
  219: [81.9, 41.1],  // 黑鳞鲛人    中国南海             16°N  115°E
  227: [77.8, 36.1],  // 云南吸血蝶  中国云南             25°N  100°E
  208: [47.2, 69.4],  // 北海海蛇    南大西洋             35°S   15°W
  205: [90.6, 73.3],  // 袋狼        澳洲塔斯马尼亚       42°S  146°E
  209: [81.1, 31.4],  // 伏牛山白蛇  中国河南伏牛山      33.5°N  112°E
  210: [78.9, 24.4],  // 阿尔玛斯人  蒙古                 46°N  104°E
  214: [85.6, 26.6],  // 天池水怪    中国长白山          42.1°N  128°E
  220: [81.5, 28.3],  // 佛光寺毛人  山西五台山           39°N  113.5°E
  203: [50.2, 21.4],  // 肯维岛人鱼  英国肯维岛          51.5°N   0.6°E
};

// ─── 接口定义 ───────────────────────────────────────────────────
interface UmaCard {
  id: number;
  name: string;
  name_en: string;
  rarity: string;
  series: string;
  has_key: boolean;
  hash?: string;
  dynamicHashes?: DynamicHash[];
  exchangeRecords?: ExchangeRecord[];
}

interface UmaCardFull extends UmaCard {
  image_url?: string;
  icon_url?: string;
  map_x?: number | null;
  map_y?: number | null;
}

interface BatchRow extends UmaCardFull {
  dirty: boolean;
}

type IconUploadStatus = 'pending' | 'unmatched' | 'uploading' | 'done' | 'error';
interface IconUploadItem {
  file: File;
  filename: string;
  cardId: number | null;   // 从文件名解析出的卡牌 ID（如 001 → 1）
  cardName: string | null;
  status: IconUploadStatus;
  url?: string;
  error?: string;
}

interface DynamicHash {
  id: number;
  hash: string;
  is_used: boolean;
  created: string;
  used_at?: string;
}

interface ExchangeRecord {
  id: number;
  hash: string;
  keys_used: number;
  shipping_address: string;
  exchange_type: string;
  status: string;
  created: string;
  processed_at?: string;
  shipping_info?: string;
}

// ─── MiniMapPreview：在世界地图上显示黄色定位点 ─────────────────
const MiniMapPreview: React.FC<{ x?: number | null; y?: number | null }> = ({ x, y }) => {
  const hasPos = x != null && y != null;
  return (
    <div style={{ position: 'relative', width: '100%', paddingTop: '50%', background: '#111', borderRadius: 4, overflow: 'hidden' }}>
      <img
        src="/uma-assets/world-map.jpg"
        alt="world map"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'fill', opacity: 0.85 }}
      />
      {hasPos && (
        <div style={{
          position: 'absolute',
          left: `${x}%`,
          top: `${y}%`,
          width: 12,
          height: 12,
          background: '#FFD700',
          borderRadius: '50%',
          border: '2px solid #fff',
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 0 6px #FFD700',
        }} />
      )}
      {!hasPos && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 12 }}>
          未设置坐标
        </div>
      )}
    </div>
  );
};

// ─── 主组件 ─────────────────────────────────────────────────────
const UmaCardManager: React.FC = () => {
  const [cards, setCards] = useState<UmaCard[]>([]);
  const [exchangeRecords, setExchangeRecords] = useState<ExchangeRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedCard, setSelectedCard] = useState<UmaCard | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<ExchangeRecord | null>(null);
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();

  // ── 批量 Tab 状态 ──
  const [batchRows, setBatchRows] = useState<BatchRow[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchSaving, setBatchSaving] = useState(false);

  // ── 批量图标上传 ──
  const [iconUploadOpen, setIconUploadOpen] = useState(false);
  const [iconUploadItems, setIconUploadItems] = useState<IconUploadItem[]>([]);
  const [iconUploading, setIconUploading] = useState(false);
  const iconFileInputRef = useRef<HTMLInputElement>(null);
  const coordFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCards();
    fetchExchangeRecords();
  }, []);

  const fetchCards = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/uma/cards?limit=100');
      const data = await response.json();
      setCards(data.cards || data);
    } catch (error) {
      message.error('获取卡牌数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchExchangeRecords = async () => {
    try {
      const response = await fetch('/api/v1/uma/exchange/records');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Exchange records response:', data);

      if (data && typeof data === 'object') {
        const records = data.records || data || [];
        if (Array.isArray(records)) {
          setExchangeRecords(records);
        } else {
          console.warn('Exchange records data is not an array:', records);
          setExchangeRecords([]);
        }
      } else {
        console.warn('Invalid exchange records response:', data);
        setExchangeRecords([]);
      }
    } catch (error) {
      console.error('Error fetching exchange records:', error);
      message.error('获取兑换记录失败: ' + (error instanceof Error ? error.message : String(error)));
      setExchangeRecords([]);
    }
  };

  const generateDynamicHash = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/uma/generate-hash/${values.cardId}/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: values.quantity })
      });

      if (response.ok) {
        const data = await response.json();
        message.success(`成功生成 ${values.quantity} 个动态hash`);

        if (data.hashes && data.hashes.length > 0) {
          exportHashesToExcel(data.hashes, values.cardId);
        }

        setGenerateModalVisible(false);
        form.resetFields();
        fetchCards();
      } else {
        message.error('生成动态hash失败');
      }
    } catch (error) {
      message.error('生成动态hash失败');
    } finally {
      setLoading(false);
    }
  };

  const exportHashesToExcel = (hashes: string[], cardId: number) => {
    const card = cards.find(c => c.id === cardId);
    const cardName = card ? card.name_en : 'Unknown';
    const timestamp = dayjs().format('MM/DD');

    const name = cardName;
    const fileName = `${name}_${timestamp}.txt`;

    const content = hashes.map(item => `https://ds2.cn/u/${item}`).join('\r\n') + '\r\n';
    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, fileName);
  };

  const updateShippingInfo = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/uma/exchange/${selectedRecord?.id}/shipping`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shipping_info: values.shipping_info,
          status: values.status
        })
      });

      if (response.ok) {
        message.success('物流信息更新成功');
        setUpdateModalVisible(false);
        updateForm.resetFields();
        fetchExchangeRecords();
      } else {
        message.error('物流信息更新失败');
      }
    } catch (error) {
      message.error('物流信息更新失败');
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 批量 Tab：加载、填充默认值、保存
  // ─────────────────────────────────────────────────────────────
  const fetchBatchCards = async () => {
    setBatchLoading(true);
    try {
      const response = await fetch('/api/v1/uma/cards?limit=200');
      const data = await response.json();
      const list: UmaCardFull[] = (data.cards || data || []).sort(
        (a: UmaCardFull, b: UmaCardFull) => a.id - b.id
      );
      setBatchRows(list.map(c => ({ ...c, dirty: false })));
    } catch (error) {
      message.error('加载批量数据失败');
    } finally {
      setBatchLoading(false);
    }
  };

  const updateBatchRow = (id: number, field: keyof BatchRow, value: any) => {
    setBatchRows(prev =>
      prev.map(row => row.id === id ? { ...row, [field]: value, dirty: true } : row)
    );
  };

  const fillDefaultPositions = () => {
    setBatchRows(prev =>
      prev.map(row => {
        const pos = DEFAULT_POSITIONS[row.id];
        if (pos) {
          return { ...row, map_x: pos[0], map_y: pos[1], dirty: true };
        }
        return row;
      })
    );
    message.success('已填入默认坐标');
  };

  const fillDefaultImages = () => {
    setBatchRows(prev =>
      prev.map((row, idx) => ({
        ...row,
        image_url: `/uma-assets/BC110-${String(idx + 1).padStart(3, '0')}-1.jpg`,
        dirty: true,
      }))
    );
    message.success('已填入默认图片路径');
  };

  // ─── 坐标表格：下载模板 & 上传解析 ──────────────────────────────────────────────

  /** 下载坐标模板 Excel，含当前 batchRows 数据（或 DEFAULT_POSITIONS 兜底） */
  const downloadCoordTemplate = () => {
    const rows = batchRows.length > 0 ? batchRows : [];
    const data = [
      ['id', '名称', '英文名', 'map_x', 'map_y'],
      ...Object.entries(DEFAULT_POSITIONS).map(([dbId, pos]) => {
        const row = rows.find(r => r.id === Number(dbId));
        return [
          Number(dbId),
          row?.name ?? '',
          row?.name_en ?? '',
          row?.map_x ?? pos[0],
          row?.map_y ?? pos[1],
        ];
      }),
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    // 列宽
    ws['!cols'] = [{ wch: 6 }, { wch: 16 }, { wch: 24 }, { wch: 10 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '坐标');
    XLSX.writeFile(wb, 'uma_coordinates.xlsx');
    message.success('坐标模板已下载');
  };

  /** 解析上传的 Excel/CSV，将 id/map_x/map_y 列写入 batchRows */
  const handleCoordFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

        // 找到列索引（容忍换行、空格、括号等多余内容）
        const header = (rows[0] as string[]).map(h => String(h ?? '').toLowerCase().replace(/\s+/g, ''));
        const idIdx = header.findIndex(h => h === 'id');
        const xIdx  = header.findIndex(h => h.includes('map_x') || h === 'x');
        const yIdx  = header.findIndex(h => h.includes('map_y') || h === 'y');

        if (idIdx < 0 || xIdx < 0 || yIdx < 0) {
          message.error('表格格式错误：需要包含 id、map_x、map_y 列');
          return;
        }

        let updated = 0;
        const coordMap: Record<number, [number, number]> = {};
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          const id = Number(row[idIdx]);
          const x  = parseFloat(row[xIdx]);
          const y  = parseFloat(row[yIdx]);
          if (!id || isNaN(x) || isNaN(y)) continue;
          coordMap[id] = [x, y];
          updated++;
        }

        setBatchRows(prev => prev.map(r => {
          if (coordMap[r.id]) {
            return { ...r, map_x: coordMap[r.id][0], map_y: coordMap[r.id][1], dirty: true };
          }
          return r;
        }));

        message.success(`已从表格读取 ${updated} 条坐标，请核查后点击「保存修改」`);
      } catch (err) {
        message.error('解析表格失败：' + (err as Error).message);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  // ─── 批量图标上传逻辑 ─────────────────────────────────────────────────────────
  /**
   * 位置（1-78，对应卡牌序号）→ 数据库真实 ID
   * 数据库 ID 从 150 开始，且顺序不完全连续（188 缺失，尾部有重排）
   */
  const POSITION_TO_DB_ID: Record<number, number> = {
    1:150, 2:151, 3:152, 4:153, 5:154, 6:155, 7:156, 8:157, 9:158, 10:159,
    11:160, 12:161, 13:162, 14:163, 15:164, 16:165, 17:166, 18:167, 19:168, 20:169,
    21:170, 22:171, 23:172, 24:173, 25:174, 26:175, 27:176, 28:177, 29:178, 30:179,
    31:180, 32:181, 33:182, 34:183, 35:184, 36:185, 37:186, 38:187,
    39:224,  // 雅各布斯兽（Jacob's Creature）
    40:189, 41:190, 42:191, 43:192, 44:193, 45:194, 46:195, 47:196, 48:197, 49:198,
    50:199,
    51:221, 52:222, 53:223,  // 空天/深海/火山汽笛
    54:200, 55:201,
    56:213, // 特瓦坎狼人
    57:215, // 苏格兰马形水鬼
    58:228, // 沼泽怪物
    59:211, // 温迪戈
    60:206, // 教皇利克怪兽
    61:216, // 夏威夷魔犬
    62:217, // 神农架野人
    63:225, // 大怪鸟
    64:226, // 昆仑山螳螂人
    65:207, // 密歇根狗人
    66:202, // 河童
    67:204, // 蜥蜴人
    68:212, // 南非飞螳人
    69:218, // 喀纳斯湖水怪
    70:219, // 黑鳞鲛人
    71:227, // 云南吸血蝶
    72:208, // 北海海蛇
    73:205, // 袋狼
    74:209, // 伏牛山白蛇
    75:210, // 阿尔玛斯人
    76:214, // 天池水怪
    77:220, // 佛光寺毛人
    78:203, // 肯维岛人鱼
  };

  // 所有合法的数据库 ID 集合，用于校验文件名
  const VALID_DB_IDS = new Set(Object.values(POSITION_TO_DB_ID));

  /**
   * 从文件名提取数据库真实 ID
   * 生成脚本直接以 DB ID 命名（如 150.png、224.png），直接解析即可
   */
  const parseDbId = (filename: string): number | null => {
    const m = filename.match(/(\d+)/);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return VALID_DB_IDS.has(n) ? n : null;
  };

  const openIconUpload = () => {
    setIconUploadItems([]);
    setIconUploadOpen(true);
  };

  const handleIconFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const items: IconUploadItem[] = files.map(file => {
      const dbId = parseDbId(file.name);
      const card = dbId ? batchRows.find(r => r.id === dbId) : null;
      return {
        file,
        filename: file.name,
        cardId: dbId,
        cardName: card?.name ?? (dbId ? `#${dbId}` : null),
        status: dbId ? 'pending' : 'unmatched',
      };
    });
    setIconUploadItems(items);
    e.target.value = '';
  };

  const handleIconUpload = async () => {
    const toUpload = iconUploadItems.filter(t => t.status === 'pending');
    if (!toUpload.length) { message.warning('没有可上传的图片'); return; }
    setIconUploading(true);
    const updated = [...iconUploadItems];

    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status !== 'pending') continue;
      updated[i] = { ...updated[i], status: 'uploading' };
      setIconUploadItems([...updated]);
      try {
        const fd = new FormData();
        fd.append('file', updated[i].file);
        fd.append('name', `icon-${String(updated[i].cardId).padStart(3, '0')}`);
        const r1 = await fetch('/api/v1/uma/cards/upload-icon', { method: 'POST', body: fd });
        const j1 = await r1.json();
        if (!j1.success) throw new Error(j1.message || '上传失败');
        const iconUrl = j1.data.url;

        // 写回数据库（用真实 DB id）
        await fetch(`/api/v1/uma/cards/${updated[i].cardId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ icon_url: iconUrl }),
        });

        updated[i] = { ...updated[i], status: 'done', url: iconUrl };
        // 同步更新 batchRows
        setBatchRows(prev => prev.map(r =>
          r.id === updated[i].cardId ? { ...r, icon_url: iconUrl, dirty: false } : r
        ));
      } catch (e: any) {
        updated[i] = { ...updated[i], status: 'error', error: e.message };
      }
      setIconUploadItems([...updated]);
    }
    setIconUploading(false);
    message.success('批量上传完成');
  };

  const saveBatchChanges = async () => {
    const dirtyRows = batchRows.filter(r => r.dirty);
    if (dirtyRows.length === 0) {
      message.info('没有需要保存的修改');
      return;
    }

    setBatchSaving(true);
    try {
      const payload = dirtyRows.map(r => ({
        id: r.id,
        image_url: r.image_url,
        icon_url: r.icon_url,
        map_x: r.map_x,
        map_y: r.map_y,
      }));

      const response = await fetch('/api/v1/uma/cards/batch', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      message.success(`保存成功：${result.updated} 条，失败 ${result.errors?.length ?? 0} 条`);

      setBatchRows(prev => prev.map(r => ({ ...r, dirty: false })));
    } catch (error) {
      message.error('批量保存失败: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setBatchSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 列定义
  // ─────────────────────────────────────────────────────────────
  const cardColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: '卡牌图',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 70,
      render: (url: string) => url
        ? <img src={url} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        : <Text type="secondary" style={{ fontSize: 12 }}>无</Text>,
    },
    {
      title: '图标',
      dataIndex: 'icon_url',
      key: 'icon_url',
      width: 70,
      render: (url: string) => url
        ? <img src={url} alt="" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee', background: '#f5f5f5' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        : <Text type="secondary" style={{ fontSize: 12 }}>无</Text>,
    },
    { title: '卡牌名称', dataIndex: 'name', key: 'name' },
    { title: '英文名', dataIndex: 'name_en', key: 'name_en' },
    {
      title: '稀有度',
      dataIndex: 'rarity',
      key: 'rarity',
      render: (rarity: string) => <Badge status="processing" text={rarity} />,
    },
    { title: '系列', dataIndex: 'series', key: 'series' },
    {
      title: '支持密钥',
      dataIndex: 'has_key',
      key: 'has_key',
      render: (hasKey: boolean) => (
        <Badge status={hasKey ? 'success' : 'default'} text={hasKey ? '是' : '否'} />
      ),
    },
    {
      title: '动态Hash数量',
      key: 'dynamic_hashes',
      render: (record: UmaCard) => <Text>{record.dynamicHashes?.length || 0}</Text>,
    },
    {
      title: '操作',
      key: 'action',
      render: (record: UmaCard) => (
        <Space size="middle">
          {record.has_key && (
            <Button
              type="primary"
              icon={<KeyOutlined />}
              onClick={() => { setSelectedCard(record); setGenerateModalVisible(true); }}
            >
              生成Hash
            </Button>
          )}
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              Modal.info({
                title: '卡牌详情',
                width: 600,
                content: (
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="ID">{record.id}</Descriptions.Item>
                    <Descriptions.Item label="中文名">{record.name}</Descriptions.Item>
                    <Descriptions.Item label="英文名">{record.name_en}</Descriptions.Item>
                    <Descriptions.Item label="稀有度">{record.rarity}</Descriptions.Item>
                    <Descriptions.Item label="系列">{record.series}</Descriptions.Item>
                    <Descriptions.Item label="支持密钥">{record.has_key ? '是' : '否'}</Descriptions.Item>
                    <Descriptions.Item label="静态Hash">{record.hash || '无'}</Descriptions.Item>
                    <Descriptions.Item label="动态Hash">{record.dynamicHashes?.length || 0} 个</Descriptions.Item>
                  </Descriptions>
                )
              });
            }}
          >
            查看
          </Button>
        </Space>
      ),
    },
  ];

  const exchangeColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: '动态Hash',
      dataIndex: 'hash',
      key: 'hash',
      render: (hash: string) => hash ? <Text code>{hash.substring(0, 10)}...</Text> : <Text type="secondary">-</Text>,
    },
    { title: '使用密钥数', dataIndex: 'keys_used', key: 'keys_used' },
    {
      title: '收货地址',
      dataIndex: 'shipping_address',
      key: 'shipping_address',
      render: (address: string) => <Text ellipsis style={{ maxWidth: 200 }}>{address}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig: Record<string, { color: any; text: string }> = {
          'pending':    { color: 'orange',  text: '待处理' },
          'processing': { color: 'blue',    text: '处理中' },
          'shipped':    { color: 'green',   text: '已发货' },
          'delivered':  { color: 'success', text: '已送达' },
          'cancelled':  { color: 'red',     text: '已取消' },
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Badge status={config.color} text={config.text} />;
      },
    },
    {
      title: '兑换时间',
      dataIndex: 'created',
      key: 'created',
      render: (time: string) => time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: ExchangeRecord) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedRecord(record);
              updateForm.setFieldsValue({ shipping_info: record.shipping_info || '', status: record.status });
              setUpdateModalVisible(true);
            }}
          >
            更新物流
          </Button>
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              Modal.info({
                title: '兑换详情',
                width: 600,
                content: (
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="兑换ID">{record.id}</Descriptions.Item>
                    <Descriptions.Item label="动态Hash">{record.hash}</Descriptions.Item>
                    <Descriptions.Item label="使用密钥数">{record.keys_used}</Descriptions.Item>
                    <Descriptions.Item label="收货地址">{record.shipping_address}</Descriptions.Item>
                    <Descriptions.Item label="兑换类型">{record.exchange_type}</Descriptions.Item>
                    <Descriptions.Item label="状态">{record.status}</Descriptions.Item>
                    <Descriptions.Item label="兑换时间">{new Date(record.created).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="物流信息">{record.shipping_info || '暂无'}</Descriptions.Item>
                  </Descriptions>
                )
              });
            }}
          >
            查看详情
          </Button>
        </Space>
      ),
    },
  ];

  // ── 批量 Tab 列 ──
  const dirtyCount = batchRows.filter(r => r.dirty).length;

  const batchColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 55 },
    { title: '名称', dataIndex: 'name', key: 'name', width: 100 },
    { title: '英文名', dataIndex: 'name_en', key: 'name_en', width: 130 },
    {
      title: '卡牌图片 (image_url)',
      dataIndex: 'image_url',
      key: 'image_url',
      render: (val: string, record: BatchRow) => (
        <Input
          size="small"
          value={val || ''}
          placeholder="/uma-assets/BC110-001-1.jpg"
          style={{ minWidth: 200 }}
          onChange={e => updateBatchRow(record.id, 'image_url', e.target.value)}
        />
      ),
    },
    {
      title: '卡牌预览',
      key: 'preview',
      width: 70,
      render: (_: any, record: BatchRow) =>
        record.image_url ? (
          <img
            src={record.image_url}
            alt=""
            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>无</Text>
        ),
    },
    {
      title: '游戏图标 (icon_url)',
      dataIndex: 'icon_url',
      key: 'icon_url',
      render: (val: string, record: BatchRow) => (
        <Input
          size="small"
          value={val || ''}
          placeholder="/uma-assets/icons/001.png"
          style={{ minWidth: 200 }}
          onChange={e => updateBatchRow(record.id, 'icon_url', e.target.value)}
        />
      ),
    },
    {
      title: '图标预览',
      key: 'icon_preview',
      width: 70,
      render: (_: any, record: BatchRow) =>
        record.icon_url ? (
          <img
            src={record.icon_url}
            alt=""
            style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee', background: '#f5f5f5' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>无</Text>
        ),
    },
    {
      title: 'map_x (0-100)',
      dataIndex: 'map_x',
      key: 'map_x',
      width: 120,
      render: (val: number, record: BatchRow) => (
        <InputNumber
          size="small"
          min={0} max={100} step={0.1}
          value={val ?? undefined}
          style={{ width: 95 }}
          onChange={v => updateBatchRow(record.id, 'map_x', v)}
        />
      ),
    },
    {
      title: 'map_y (0-100)',
      dataIndex: 'map_y',
      key: 'map_y',
      width: 120,
      render: (val: number, record: BatchRow) => (
        <InputNumber
          size="small"
          min={0} max={100} step={0.1}
          value={val ?? undefined}
          style={{ width: 95 }}
          onChange={v => updateBatchRow(record.id, 'map_y', v)}
        />
      ),
    },
    {
      title: '地图预览',
      key: 'map_preview',
      width: 120,
      render: (_: any, record: BatchRow) => (
        <div style={{ width: 110 }}>
          <MiniMapPreview x={record.map_x} y={record.map_y} />
        </div>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────
  // 渲染
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="uma-card-manager">
      <Title level={2}>UMA卡牌管理</Title>

      <Tabs defaultActiveKey="cards">
        {/* ── 卡牌管理 ── */}
        <TabPane tab="卡牌管理" key="cards">
          <Card>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col>
                <Button type="primary" icon={<ReloadOutlined />} onClick={fetchCards} loading={loading}>
                  刷新数据
                </Button>
              </Col>
            </Row>
            <Table
              dataSource={cards}
              columns={cardColumns}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
            />
          </Card>
        </TabPane>

        {/* ── 兑换记录 ── */}
        <TabPane tab="兑换记录" key="exchange">
          <Card>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col>
                <Button type="primary" icon={<ReloadOutlined />} onClick={fetchExchangeRecords} loading={loading}>
                  刷新记录
                </Button>
              </Col>
            </Row>
            <Table
              dataSource={exchangeRecords || []}
              columns={exchangeColumns}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
            />
          </Card>
        </TabPane>

        {/* ── 批量坐标/图片 ── */}
        <TabPane tab="批量坐标/图片" key="batch">
          <Card>
            <Row gutter={8} style={{ marginBottom: 16 }} align="middle">
              <Col>
                <Button icon={<ReloadOutlined />} onClick={fetchBatchCards} loading={batchLoading}>
                  加载/刷新
                </Button>
              </Col>
              <Col>
                <Button onClick={fillDefaultPositions} disabled={batchRows.length === 0}>
                  一键填入默认坐标
                </Button>
              </Col>
              <Col>
                <Button onClick={fillDefaultImages} disabled={batchRows.length === 0}>
                  一键填入BC110图片路径
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={batchSaving}
                  disabled={dirtyCount === 0}
                  onClick={saveBatchChanges}
                >
                  保存修改{dirtyCount > 0 ? `（${dirtyCount} 条）` : ''}
                </Button>
              </Col>
              <Col>
                <Button
                  icon={<UploadOutlined />}
                  onClick={openIconUpload}
                  disabled={batchRows.length === 0}
                >
                  批量上传图标
                </Button>
              </Col>
              <Col>
                <Button onClick={downloadCoordTemplate}>
                  下载坐标模板
                </Button>
              </Col>
              <Col>
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => coordFileInputRef.current?.click()}
                  disabled={batchRows.length === 0}
                >
                  上传坐标表格
                </Button>
                <input
                  ref={coordFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={handleCoordFileChange}
                />
              </Col>
            </Row>

            {batchRows.length === 0 && !batchLoading && (
              <div style={{ textAlign: 'center', color: '#888', padding: '32px 0' }}>
                点击「加载/刷新」按钮载入卡牌数据
              </div>
            )}

            {batchRows.length > 0 && (
              <Table
                dataSource={batchRows}
                columns={batchColumns}
                rowKey="id"
                loading={batchLoading}
                pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '40', '78'] }}
                scroll={{ x: true }}
                rowClassName={record => (record as BatchRow).dirty ? 'batch-row-dirty' : ''}
                size="small"
              />
            )}
          </Card>
        </TabPane>
      </Tabs>

      {/* ── 生成动态Hash ── */}
      <Modal
        title={`生成动态Hash - ${selectedCard?.name}`}
        open={generateModalVisible}
        onCancel={() => { setGenerateModalVisible(false); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={generateDynamicHash}>
          <Form.Item name="cardId" label="卡牌ID" initialValue={selectedCard?.id}>
            <Input disabled />
          </Form.Item>
          <Form.Item name="quantity" label="生成数量" rules={[{ required: true, message: '请输入生成数量' }]}>
            <InputNumber min={1} max={1000} placeholder="请输入生成数量" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>生成并导出</Button>
              <Button onClick={() => { setGenerateModalVisible(false); form.resetFields(); }}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── 更新物流信息 ── */}
      <Modal
        title="更新物流信息"
        open={updateModalVisible}
        onCancel={() => { setUpdateModalVisible(false); updateForm.resetFields(); }}
        footer={null}
      >
        <Form form={updateForm} layout="vertical" onFinish={updateShippingInfo}>
          <Form.Item name="status" label="订单状态" rules={[{ required: true, message: '请选择订单状态' }]}>
            <Select>
              <Option value="pending">待处理</Option>
              <Option value="processing">处理中</Option>
              <Option value="shipped">已发货</Option>
              <Option value="delivered">已送达</Option>
              <Option value="cancelled">已取消</Option>
            </Select>
          </Form.Item>
          <Form.Item name="shipping_info" label="物流信息" rules={[{ required: true, message: '请输入物流信息' }]}>
            <Input.TextArea rows={4} placeholder="请输入物流公司、快递单号等信息" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>更新</Button>
              <Button onClick={() => { setUpdateModalVisible(false); updateForm.resetFields(); }}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── 批量图标上传弹窗 ── */}
      <input
        ref={iconFileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleIconFilesChange}
      />
      <Modal
        title="批量上传 UMA 图标"
        open={iconUploadOpen}
        width={700}
        onCancel={() => { if (!iconUploading) setIconUploadOpen(false); }}
        footer={[
          <Button key="sel" icon={<UploadOutlined />} onClick={() => iconFileInputRef.current?.click()} disabled={iconUploading}>
            选择图片（可多选）
          </Button>,
          <Button key="go" type="primary" loading={iconUploading}
            disabled={!iconUploadItems.some(i => i.status === 'pending')}
            onClick={handleIconUpload}
          >
            开始上传
          </Button>,
          <Button key="close" onClick={() => setIconUploadOpen(false)} disabled={iconUploading}>关闭</Button>,
        ]}
      >
        <p style={{ color: '#888', marginBottom: 12 }}>
          文件名中须包含卡牌序号（如 <code>cartoon-001.png</code>、<code>icon003.jpg</code>、<code>3.png</code>），系统自动匹配对应卡牌并更新 icon_url。
        </p>
        {iconUploadItems.length > 0 && (
          <>
            <div style={{ marginBottom: 8 }}>
              共 {iconUploadItems.length} 张，
              待上传 {iconUploadItems.filter(i => i.status === 'pending').length}，
              已完成 {iconUploadItems.filter(i => i.status === 'done').length}，
              未匹配 {iconUploadItems.filter(i => i.status === 'unmatched').length}
            </div>
            <Table
              size="small"
              pagination={false}
              scroll={{ y: 360 }}
              dataSource={iconUploadItems}
              rowKey="filename"
              columns={[
                { title: '文件名', dataIndex: 'filename', key: 'filename', ellipsis: true },
                {
                  title: '匹配卡牌',
                  key: 'card',
                  render: (_: any, r: IconUploadItem) =>
                    r.cardName ? `${r.cardName} (#${r.cardId})` : <Tag color="orange">未匹配</Tag>,
                },
                {
                  title: '预览',
                  key: 'preview',
                  width: 60,
                  render: (_: any, r: IconUploadItem) =>
                    r.status === 'done' && r.url
                      ? <img src={r.url} alt="" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4, background: '#f5f5f5' }} />
                      : null,
                },
                {
                  title: '状态',
                  key: 'status',
                  width: 100,
                  render: (_: any, r: IconUploadItem) => {
                    const map: Record<IconUploadStatus, { color: string; text: string }> = {
                      pending:   { color: 'blue',    text: '待上传' },
                      unmatched: { color: 'orange',  text: '未匹配' },
                      uploading: { color: 'cyan',    text: '上传中' },
                      done:      { color: 'green',   text: '完成' },
                      error:     { color: 'red',     text: '失败' },
                    };
                    const cfg = map[r.status];
                    return <Tag color={cfg.color}>{cfg.text}{r.error ? `：${r.error}` : ''}</Tag>;
                  },
                },
              ]}
            />
          </>
        )}
        {iconUploadItems.length === 0 && (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '32px 0' }}>
            请先点击「选择图片」选取图标文件
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UmaCardManager;
